import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, Drawer, Icon, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';
import { formatINR, formatIndianDate } from '@kollez/shared';
import { usePlatformTenant, useReactivateTenant, useSetTenantAiCap, useTenantHealthSummary, useTenantAiUsage, useTenantSubscriptionHistory, type TenantSubscriptionHistory } from '@/lib/queries';

/**
 * /platform/tenants/:id — direct port of kollez/pages/superadmin/tenant.html.
 *   5 tabs: Usage / Plan+Billing / Admins / Features / Activity.
 */
type Tab = 'usage' | 'plan' | 'people' | 'flags' | 'log';

/** Keys must match the ones in the FeatureFlag catalog on the server. */
const FEATURES: Array<{ key: string; label: string; sub: string; defaultOn: boolean }> = [
  { key: 'assessments_module', label: 'Assessments module', sub: 'Chat exams, question bank, integrity review', defaultOn: true },
  { key: 'nexie_telugu_beta', label: 'Nexie in Telugu', sub: 'Instructions and answers in Telugu (beta)', defaultOn: true },
  { key: 'whatsapp_own_number', label: 'WhatsApp alerts', sub: "Uses the tenant's own WhatsApp number", defaultOn: true },
  { key: 'adaptive_exams', label: 'Adaptive exams', sub: 'Allowed for practice and baselines only', defaultOn: true },
  { key: 'alumni_network', label: 'Alumni network', sub: 'Early access', defaultOn: false },
  { key: 'sso_google', label: 'SSO (Google Workspace)', sub: 'Needs their IT admin to approve', defaultOn: false },
];

interface FlagRow {
  key: string;
  rollout: 'off' | 'per-tenant' | 'all';
  perTenantOverrides: Array<{ tenant: string; enabled: boolean }>;
}

/** Read the effective per-tenant state from the flags catalog. */
function effectiveState(flag: FlagRow | undefined, tenantId: string, def: boolean): { enabled: boolean; locked: boolean } {
  if (!flag) return { enabled: def, locked: false };
  if (flag.rollout === 'all') return { enabled: true, locked: true };
  if (flag.rollout === 'off') return { enabled: false, locked: true };
  const ov = flag.perTenantOverrides.find((o) => String(o.tenant) === String(tenantId));
  return { enabled: ov?.enabled ?? def, locked: false };
}

export function PlatformTenantDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  // F-095: live tenant + subscription (was hardcoded to Godavari/Coastal demo data).
  const detail = usePlatformTenant(id);
  const tenant = detail.data?.tenant;
  const subscription = detail.data?.subscription;
  const [tab, setTab] = useState<Tab>('usage');
  const [capPaused, setCapPaused] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const qc = useQueryClient();

  // Live flag catalog. Falls back to the design defaults if the API is unreachable.
  const flagsQuery = useQuery({
    queryKey: ['platform', 'flags'],
    queryFn: () => apiGet<{ flags: FlagRow[] }>('/platform/flags'),
  });
  const flagByKey = new Map((flagsQuery.data?.flags ?? []).map((f) => [f.key, f]));

  const setOverride = useMutation({
    mutationFn: (input: { key: string; description: string; enabled: boolean }) => {
      const flag = flagByKey.get(input.key);
      // If the flag doesn't exist yet or isn't in per-tenant mode, flip it to per-tenant first.
      const ensure = flag && flag.rollout === 'per-tenant'
        ? Promise.resolve()
        : apiPost('/platform/flags', { key: input.key, description: input.description, rollout: 'per-tenant' });
      return ensure.then(() => apiPost(`/platform/flags/${input.key}/override`, { tenant: id, enabled: input.enabled }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'flags'] }),
  });

  // F-095: pull identity from the live tenant. Fall back to sensible strings while loading.
  const name = tenant?.name ?? 'Tenant';
  const short = (tenant?.name ?? 'T').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const planLabel = subscription?.plan?.name ?? (tenant?.tenantType ?? 'Tenant');
  const createdOn = tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—';
  const eyebrow = `${planLabel} · since ${createdOn}`;
  const region = tenant?.dataRegion ?? '—';
  const sub = `Region: ${region}${tenant?.dpdpConsentVersion ? ` · DPDP consent ${tenant.dpdpConsentVersion}` : ''}`;
  const monthlyBudget = tenant?.aiCap?.monthlyBudgetInr ?? 0;
  const spent = tenant?.aiCap?.spentThisMonth ?? 0;
  const aiPct = monthlyBudget > 0 ? Math.min(100, Math.round((spent / monthlyBudget) * 100)) : 0;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="flex items-center gap-4 min-w-0">
          <span className="logo-tile" style={{ width: 56, height: 56, fontSize: '1rem' }}>{short}</span>
          <div className="ph-text">
            <p className="eyebrow">{eyebrow}</p>
            <h1 className="h-page">{name}</h1>
            <p className="subhead">{sub}</p>
          </div>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline" onClick={() => setViewAsOpen(true)}><Icon name="eye" size={14} />View as</button>
          {tenant?.status === 'SUSPENDED'
            ? <ReactivateButton tenantId={id ?? ''} tenantName={name} />
            : <button className="btn btn-ghost btn-icon" aria-label="Suspend" onClick={() => setSuspendOpen(true)}><Icon name="more-horizontal" size={14} /></button>}
        </div>
      </div>

      {/* Auto-pause when the tenant has crossed 100% of its AI budget. F-095: this used to
          be hardcoded to the 'coastal' demo slug. */}
      {(capPaused || (monthlyBudget > 0 && spent >= monthlyBudget)) && (
        <div className="error-banner">
          <Icon name="zap-off" size={16} />
          <div className="flex-1">
            <b>Nexie is paused for this tenant.</b> The ₹{monthlyBudget.toLocaleString('en-IN')} monthly AI cap has been reached (spent ₹{spent.toLocaleString('en-IN')}).
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setCapOpen(true)}>Raise cap</button>
        </div>
      )}

      {/* F-095: KPIs from real tenant + subscription. Contract value derives from
          `subscription.plan.price` × seats when available. */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi">
          <span className="kpi-label">Tenant type</span>
          <span className="kpi-value">{tenant?.tenantType ?? '—'}</span>
          <span className="kpi-meta">Status {tenant?.status ?? '—'}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Seats</span>
          <span className="kpi-value">{subscription?.seats?.used ?? 0}<span className="text-ink-tertiary text-base"> / {subscription?.seats?.allowed ?? '∞'}</span></span>
          <span className="kpi-meta">Plan {subscription?.plan?.name ?? 'None'}</span>
        </div>
        <div className="card kpi" data-tone={subscription?.plan?.price?.amount ? 'success' : undefined}>
          <span className="kpi-label">Plan price</span>
          <span className="kpi-value">{subscription?.plan?.price?.amount
            ? `₹${(subscription.plan.price.amount).toLocaleString('en-IN')}`
            : 'Custom'}</span>
          <span className="kpi-meta">{subscription?.plan?.price?.unit?.replaceAll('_', ' ') ?? '—'}</span>
        </div>
        <div className="card kpi" data-tone={aiPct >= 80 ? 'warning' : undefined}>
          <span className="kpi-label">AI this month</span>
          <span className="kpi-value">{aiPct}%</span>
          <span className="kpi-meta">₹{spent.toLocaleString('en-IN')} of ₹{monthlyBudget.toLocaleString('en-IN')}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Data region</span>
          <span className="kpi-value">{region}</span>
          <span className="kpi-meta">{tenant?.address?.country ?? 'India'}</span>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {([['usage', 'Usage'], ['plan', 'Plan and billing'], ['people', 'Admins'], ['flags', 'Features'], ['log', 'Activity']] as const).map(([k, l]) => (
          <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'usage' && (
        <>
          <TenantHealthUsage tenantId={id} />
          <TenantAiUsagePanel tenantId={id} />
        </>
      )}

      {tab === 'plan' && (
        <TenantPlanBilling
          tenantId={id}
          onOpenPlans={() => nav('/platform/plans')}
          onOpenCap={() => setCapOpen(true)}
        />
      )}

      {tab === 'people' && (
        <section className="card">
          <p className="helper p-4 m-0">
            Live admin roster for this tenant is under <Link to={`/platform/users?tenant=${id}`}>/platform/users</Link> filtered
            by tenant. Hardcoded name rows were dropped (F-089). Change an owner only with a signed request from the institution.
          </p>
        </section>
      )}

      {tab === 'flags' && (
        <section className="card">
          {FEATURES.map((f) => {
            const flagRow = flagByKey.get(f.key);
            const eff = effectiveState(flagRow, id ?? '', f.defaultOn);
            return (
              <div key={f.key} className="flex items-center gap-3 px-5 py-3 border-b border-line-tertiary last:border-0">
                <div className="flex-1">
                  <div className="font-medium text-sm">{f.label}</div>
                  {f.sub && <div className="cell-sub">{f.sub}</div>}
                  {eff.locked && (
                    <div className="cell-sub">
                      <span className="badge badge-outline mt-1">Locked by platform rollout: {flagRow?.rollout}</span>
                    </div>
                  )}
                </div>
                <span className="switch">
                  <input
                    type="checkbox"
                    checked={eff.enabled}
                    disabled={eff.locked || setOverride.isPending}
                    aria-label={f.label}
                    onChange={(e) => setOverride.mutate(
                      { key: f.key, description: f.sub, enabled: e.target.checked },
                      {
                        onSuccess: () => toast.show({ message: `${f.label} ${e.target.checked ? 'enabled' : 'disabled'} for this tenant.` }),
                        onError: (err) => toast.show({ tone: 'warning', message: (err as Error)?.message ?? 'Failed to update flag' }),
                      },
                    )}
                  />
                  <span />
                </span>
              </div>
            );
          })}
          <p className="helper p-3 m-0">
            To roll out a feature globally or turn it off everywhere, use{' '}
            <Link className="link" to="/platform/flags">Feature flags</Link>.
          </p>
        </section>
      )}

      {tab === 'log' && (
        <section className="card card-pad">
          <p className="cell-sub m-0">
            The tenant-scoped activity feed lives on the full audit log, filtered by this
            tenant id. Previous placeholders (Kiran Ops viewed as X · 6 min · ticket #4807)
            were fabricated. Open the filtered log:
          </p>
          <Link className="link text-sm mt-2" to={`/platform/audit?tenant=${id ?? ''}`}>Open filtered audit log →</Link>
        </section>
      )}

      <CapDrawer
        open={capOpen}
        currentInr={monthlyBudget}
        tenantId={id ?? ''}
        onClose={() => setCapOpen(false)}
        onSaved={() => { setCapOpen(false); setCapPaused(false); toast.show({ message: 'AI cap saved.' }); }}
      />

      <Drawer open={viewAsOpen} onClose={() => setViewAsOpen(false)} title="View as someone in this tenant" size="md">
        <div className="grid gap-4">
          <p className="cell-sub m-0">Impersonation is per-user, not per-tenant. Pick the specific user from the directory below; on the user's drawer, enter a reason (min 10 chars) and start the 15-minute session.</p>
          <div className="notice notice-info"><Icon name="eye" size={14} /><div>15 minutes hard cap. The tenant admin is emailed at start. Every action is logged with your id as impersonatedBy.</div></div>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-primary ml-auto" onClick={() => { setViewAsOpen(false); nav(`/platform/users?tenant=${id}`); }}>Open users of this tenant</button>
        </div>
      </Drawer>

      <SuspendConfirm
        open={suspendOpen}
        tenantId={id ?? ''}
        tenantName={name}
        typedWord={(tenant?.name ?? 'TENANT').split(' ')[0]!.toUpperCase()}
        onClose={() => setSuspendOpen(false)}
        onDone={() => { setSuspendOpen(false); toast.show({ message: `${name} suspended.` }); }}
      />
    </div>
  );
}

function CapDrawer({ open, currentInr, tenantId, onClose, onSaved }: {
  open: boolean; currentInr: number; tenantId: string; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const set = useSetTenantAiCap();
  const [amt, setAmt] = useState('');
  const [reason, setReason] = useState('');
  const disabled = !amt || Number(amt) < 0 || reason.trim().length < 5 || set.isPending;
  return (
    <Drawer open={open} onClose={onClose} title="Monthly AI cap" size="md">
      <div className="grid gap-4">
        <p className="cell-sub m-0">Current cap: ₹{currentInr.toLocaleString('en-IN')}/month.</p>
        <label className="grid gap-1.5">
          <span className="label">New monthly cap (₹)</span>
          <input className="input" type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder={String(currentInr)} />
        </label>
        <label className="grid gap-1.5">
          <span className="label">Reason (min 5 chars) — recorded on the AuditLog</span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ticket #4841 · exam-season surge" />
        </label>
      </div>
      <div className="drawer-foot">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary ml-auto"
          disabled={disabled}
          onClick={() => {
            set.mutate(
              { tenantId, monthlyBudgetInr: Number(amt), reason: reason.trim() },
              {
                onSuccess: () => onSaved(),
                onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
              },
            );
          }}
        >
          {set.isPending ? 'Saving…' : 'Save cap'}
        </button>
      </div>
    </Drawer>
  );
}

function SuspendConfirm({ open, tenantId, tenantName, typedWord, onClose, onDone }: {
  open: boolean; tenantId: string; tenantName: string; typedWord: string; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`Suspend ${tenantName}?`}
      body={(
        <div className="grid gap-2">
          <p className="m-0">Every user in this tenant keeps their session until refresh (up to 15 min access + 7-day refresh) — Kollez F49. Data is kept.</p>
          <label className="grid gap-1.5">
            <span className="label">Reason (min 10 chars)</span>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Non-payment past grace / security incident" />
          </label>
        </div>
      )}
      cta="Suspend"
      tone="danger"
      typeToConfirm={typedWord}
      onConfirm={async () => {
        if (reason.trim().length < 10) {
          toast.show({ tone: 'warning', message: 'Reason must be at least 10 characters.' });
          return;
        }
        try {
          await apiPost(`/platform/tenants/${tenantId}/suspend`, {
            reason: reason.trim(),
            typedConfirmation: typedWord,
          });
          onDone();
        } catch (e) {
          toast.show({ tone: 'warning', message: (e as Error).message });
        }
      }}
    />
  );
}

function ReactivateButton({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const toast = useToast();
  const react = useReactivateTenant();
  return (
    <button
      className="btn btn-primary"
      disabled={react.isPending}
      onClick={() => {
        const reason = window.prompt('Reason for re-activating (min 5 chars) — recorded on the AuditLog');
        if (!reason || reason.trim().length < 5) return;
        react.mutate(
          { tenantId, reason: reason.trim() },
          {
            onSuccess: () => toast.show({ message: `${tenantName} re-activated.` }),
            onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
          },
        );
      }}
    >
      {react.isPending ? 'Re-activating…' : 'Re-activate'}
    </button>
  );
}

/**
 * Pass 11 relationship endpoint — /platform/tenants/:id/health-summary.
 * Renders the composite that used to be honestly stubbed on the Usage tab:
 * counts (users, colleges, drives, applications, offers, memberships) with
 * a "last 30d" delta, last-activity timestamp, and onboarding-signal
 * checklist. Every cross-tenant read is server-audited.
 */
function TenantHealthUsage({ tenantId }: { tenantId: string | undefined }) {
  const q = useTenantHealthSummary(tenantId);
  const data = q.data;

  if (q.isLoading) {
    return (
      <section className="card card-pad">
        <p className="cell-sub m-0">Loading health…</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="card card-pad">
        <p className="cell-sub m-0">Couldn't load health for this tenant.</p>
      </section>
    );
  }

  const lastAgo = data.lastActivityAt
    ? Math.floor((Date.now() - new Date(data.lastActivityAt).getTime()) / 86_400_000)
    : null;
  const signals: Array<[string, boolean, string]> = [
    ['≥1 admin user', data.onboardingSignals.hasUsers, 'Invite the first admin via the Admins tab.'],
    ['≥3 users', data.onboardingSignals.hasEnoughUsers, 'A single admin is a bus factor of one.'],
    ['≥1 college seeded', data.onboardingSignals.hasColleges, 'GROUP tenants without a college can\'t host drives.'],
    ['≥1 drive posted', data.onboardingSignals.hasAnyDrive, 'The TPO hasn\'t started the golden path yet.'],
    ['≥1 offer accepted', data.onboardingSignals.hasAnyOfferAccepted, 'The pipeline hasn\'t closed a placement yet.'],
    ['Active in last 30d', data.onboardingSignals.activeLast30d, 'No drives / apps / offers moved recently.'],
  ];

  return (
    <div className="stack">
      <section className="card card-pad grid gap-4">
        <div className="card-head" style={{ padding: 0 }}>
          <div>
            <h2 className="h-section">Usage &amp; activity</h2>
            <p className="helper mt-0.5">
              Live counts across this tenant's users, colleges, drives, applications, and offers.
              {data.lastActivityAt && (
                <> Last activity {lastAgo === 0 ? 'today' : lastAgo === 1 ? 'yesterday' : `${lastAgo} days ago`}.</>
              )}
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Users</span>
            <span className="kpi-value">{data.counts.users.toLocaleString('en-IN')}</span>
            <span className="kpi-meta">{data.counts.activeMemberships} active memberships</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Colleges</span>
            <span className="kpi-value">{data.counts.colleges.toLocaleString('en-IN')}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Drives</span>
            <span className="kpi-value">{data.counts.drives.toLocaleString('en-IN')}</span>
            <span className="kpi-meta">
              {data.counts.drivesLast30d > 0 ? `+${data.counts.drivesLast30d} in 30d` : 'none in 30d'}
            </span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Applications</span>
            <span className="kpi-value">{data.counts.applications.toLocaleString('en-IN')}</span>
            <span className="kpi-meta">
              {data.counts.applicationsLast30d > 0 ? `+${data.counts.applicationsLast30d} in 30d` : 'none in 30d'}
            </span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Offers accepted</span>
            <span className="kpi-value">{data.counts.offersAccepted.toLocaleString('en-IN')}</span>
            <span className="kpi-meta">
              {data.counts.offersAcceptedLast30d > 0 ? `+${data.counts.offersAcceptedLast30d} in 30d` : 'none in 30d'}
            </span>
          </div>
        </div>
      </section>

      <section className="card card-pad grid gap-2">
        <h2 className="h-section">Onboarding signals</h2>
        <p className="helper m-0">
          Quick health check against the golden-path milestones. Green = met, amber = not yet.
        </p>
        <ol className="stack mt-2">
          {signals.map(([label, ok, hint]) => (
            <li key={label} className="flex items-start gap-2">
              <span className={`badge ${ok ? 'badge-success' : 'badge-warning'}`}>
                {ok ? '✓' : '·'}
              </span>
              <div className="min-w-0">
                <div className="font-medium">{label}</div>
                {!ok && <div className="cell-sub">{hint}</div>}
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/**
 * Pass 5 relationship — /platform/tenants/:id/ai-usage. Renders the caller-authoritative
 * Nexie spend snapshot: cap remaining, action breakdown, top-10 actors by call count, and
 * a 30-day daily histogram. Every read is audited server-side. Section self-hides when
 * the tenant has neither a cap nor any Nexie audit rows in the window.
 */
function TenantAiUsagePanel({ tenantId }: { tenantId: string | undefined }) {
  const [win, setWin] = useState<'this_month' | '7d' | '30d' | '90d'>('this_month');
  const q = useTenantAiUsage(tenantId, win);
  const data = q.data;

  if (q.isLoading) return null;
  if (!data) return null;

  const cap = data.aiCap;
  const totalCalls = data.breakdown.drafted + data.breakdown.sent + data.breakdown.discarded;
  const utilPct = cap.monthlyBudgetInr > 0
    ? Math.min(100, Math.round((cap.spentThisMonth / cap.monthlyBudgetInr) * 100))
    : 0;
  const maxDaily = Math.max(1, ...data.daily.map((d) => d.count));

  if (cap.monthlyBudgetInr === 0 && totalCalls === 0) return null;

  return (
    <section className="card card-pad grid gap-4 mt-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="h-section m-0">Nexie AI usage</h2>
          <p className="helper mt-0.5">
            ₹{cap.spentThisMonth.toLocaleString('en-IN')} spent of ₹{cap.monthlyBudgetInr.toLocaleString('en-IN')} monthly cap
            {cap.resetsOn && <> · resets {new Date(cap.resetsOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</>}
          </p>
        </div>
        <div className="flex gap-1">
          {(['this_month', '7d', '30d', '90d'] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={`tag ${win === w ? 'tag-active' : ''}`}
              aria-pressed={win === w}
              onClick={() => setWin(w)}
            >
              {w === 'this_month' ? 'This month' : w}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Utilisation</span>
          <span className="kpi-value">{utilPct}%</span>
          <span className="kpi-meta">₹{cap.remainingInr.toLocaleString('en-IN')} remaining</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Drafted</span>
          <span className="kpi-value">{data.breakdown.drafted}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Sent</span>
          <span className="kpi-value">{data.breakdown.sent}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Discarded</span>
          <span className="kpi-value">{data.breakdown.discarded}</span>
        </div>
      </div>

      {data.daily.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Daily Nexie actions ({data.daily.length} days)</div>
          <div className="flex items-end gap-[3px]" style={{ height: 60 }}>
            {data.daily.map((d) => (
              <span
                key={d.day}
                title={`${d.day}: ${d.count}`}
                style={{
                  flex: 1,
                  height: `${Math.max(4, Math.round((d.count / maxDaily) * 100))}%`,
                  background: 'rgb(var(--ink-accent) / .7)',
                  borderRadius: '2px 2px 0 0',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {data.topActors.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Top actors</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>User</th><th className="r">Calls</th></tr></thead>
              <tbody>
                {data.topActors.map((a) => (
                  <tr key={a.userId}>
                    <td>
                      <div className="cell-title">{a.name ?? '—'}</div>
                      {a.email && <div className="cell-sub">{a.email}</div>}
                    </td>
                    <td className="r">{a.count.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Pass 6 relationship — /platform/tenants/:id/subscription-history. Composite
 * (subscription + plan snapshot + invoice list + rollup + audit timeline). Replaces
 * the honest stub on the Plan tab that pointed at this endpoint.
 */
function TenantPlanBilling({
  tenantId,
  onOpenPlans,
  onOpenCap,
}: {
  tenantId: string | undefined;
  onOpenPlans: () => void;
  onOpenCap: () => void;
}) {
  const q = useTenantSubscriptionHistory(tenantId);
  const data: TenantSubscriptionHistory | undefined = q.data;

  if (q.isLoading) return <section className="card card-pad"><p className="cell-sub m-0">Loading…</p></section>;
  if (!data) return <section className="card card-pad"><p className="cell-sub m-0">Couldn't load subscription history.</p></section>;

  const sub = data.subscription;
  const plan = sub?.plan;
  const paidAmount = data.invoicesSummary.byStatus['Paid']?.amountInr ?? 0;
  const overdueAmount = data.invoicesSummary.byStatus['Overdue']?.amountInr ?? 0;
  const outstandingAmount = data.invoicesSummary.byStatus['Sent']?.amountInr ?? 0;

  return (
    <div className="stack">
      <section className="grid gap-5 lg:grid-cols-2">
        <div className="card card-pad grid gap-3">
          <div className="flex items-baseline justify-between">
            <h2 className="h-section m-0">Plan</h2>
            {sub?.status && <span className={`badge ${sub.status === 'ACTIVE' ? 'badge-success' : sub.status === 'PAST_DUE' ? 'badge-danger' : ''}`}>{sub.status}</span>}
          </div>
          {sub && plan ? (
            <dl className="dl">
              <dt>Plan</dt><dd>{plan.name ?? plan.code ?? '—'}</dd>
              {plan.price?.amount && (<>
                <dt>Price</dt>
                <dd>{formatINR(plan.price.amount)} <span className="cell-sub">{plan.price.unit?.replace(/_/g, ' ')}</span></dd>
              </>)}
              {sub.term?.startsAt && sub.term?.endsAt && (<>
                <dt>Term</dt>
                <dd>{formatIndianDate(new Date(sub.term.startsAt))} → {formatIndianDate(new Date(sub.term.endsAt))}</dd>
              </>)}
              {sub.seats && (<>
                <dt>Seats</dt>
                <dd>{sub.seats.used ?? 0} of {sub.seats.allowed ?? '—'}</dd>
              </>)}
              {sub.po?.number && (<>
                <dt>PO</dt>
                <dd className="mono">{sub.po.number}</dd>
              </>)}
              {sub.billing?.gstin && (<>
                <dt>GSTIN</dt>
                <dd className="mono">{sub.billing.gstin}</dd>
              </>)}
              {sub.aiCapOverride !== undefined && (<>
                <dt>AI cap</dt>
                <dd>{formatINR(sub.aiCapOverride)}<span className="cell-sub"> /mo (override)</span></dd>
              </>)}
            </dl>
          ) : (
            <p className="cell-sub m-0">No active subscription. Provision one via the Plans catalog.</p>
          )}
          <div>
            <button className="btn btn-outline btn-sm" onClick={onOpenPlans}>Open plans catalog</button>{' '}
            <button className="btn btn-outline btn-sm ml-2" onClick={onOpenCap}>Change AI cap</button>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="h-section">Invoice rollup</h2>
            <Link className="link text-sm" to={`/platform/invoices?tenant=${tenantId ?? ''}`}>Full list</Link>
          </div>
          <div className="card-pad grid gap-3">
            <div className="grid gap-3 grid-cols-2">
              <div className="card kpi"><span className="kpi-label">Paid</span><span className="kpi-value">{formatINR(paidAmount)}</span></div>
              <div className="card kpi"><span className="kpi-label">Overdue</span><span className="kpi-value">{formatINR(overdueAmount)}</span></div>
              <div className="card kpi"><span className="kpi-label">Outstanding</span><span className="kpi-value">{formatINR(outstandingAmount)}</span></div>
              <div className="card kpi"><span className="kpi-label">Invoices</span><span className="kpi-value">{data.invoicesSummary.total}</span></div>
            </div>
          </div>
        </div>
      </section>

      {data.invoices.length > 0 && (
        <section className="card">
          <div className="card-head"><h2 className="h-section">Recent invoices</h2></div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Invoice</th><th>Issued</th><th>Due</th><th>Status</th><th className="r">Amount</th></tr></thead>
              <tbody>
                {data.invoices.slice(0, 10).map((inv) => (
                  <tr key={inv._id}>
                    <td className="cell-title mono">{inv.invoiceNumber ?? inv._id.slice(-6)}</td>
                    <td className="cell-sub">{inv.issuedAt ? formatIndianDate(new Date(inv.issuedAt)) : '—'}</td>
                    <td className="cell-sub">{inv.dueAt ? formatIndianDate(new Date(inv.dueAt)) : '—'}</td>
                    <td>
                      <span className={`badge ${inv.status === 'Paid' ? 'badge-success' : inv.status === 'Overdue' ? 'badge-danger' : ''}`}>
                        {inv.status ?? '—'}
                      </span>
                    </td>
                    <td className="r">{inv.totalInr ? formatINR(inv.totalInr) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.timeline.length > 0 && (
        <section className="card">
          <div className="card-head"><h2 className="h-section">Billing timeline</h2></div>
          <ol className="stack p-4 m-0">
            {data.timeline.slice(0, 10).map((t) => (
              <li key={t._id} className="flex items-start gap-2">
                <span className="badge">{t.action}</span>
                <span className="cell-sub ml-auto">{formatIndianDate(new Date(t.at))}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
